"use data";

import { zodResolver } from "@hookform/resolvers/zod";
import {
	useAddVoucher,
	useCart,
	useRemoveVoucher,
} from "@trydig/centra/client";
import { clsx } from "@trydig/ui";
import type { ComponentProps } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Chevron } from "@/components/icons/Chevron";
import { XIcon } from "@/components/icons/XIcon";
import { Translate } from "@/components/Translate";
import { Button } from "@/components/ui/Button";
import { IconButton } from "@/components/ui/IconButton";
import { Input } from "@/components/ui/Input";
import { Skeleton } from "@/components/ui/Skeleton";
import { getUserData } from "@/features/global/user_data";
import { useTranslations } from "@/i18n/client";
import { sendGTMEvent } from "@/lib/gtm/utils";
import { priceFormatter } from "@/lib/utils";
import styles from "./CartSummary.module.css";

const AddVoucher = () => {
	const t = useTranslations();
	const { mutate, isPending } = useAddVoucher();
	const { isFetching } = useCart();

	const voucherSchema = z.object({
		code: z.string().min(1, t("Voucher code is required")).trim(),
	});

	type VoucherFormData = z.infer<typeof voucherSchema>;

	const {
		register,
		handleSubmit,
		formState: { errors },
		setError,
		reset,
	} = useForm<VoucherFormData>({
		resolver: zodResolver(voucherSchema),
	});

	const onSubmit = (data: VoucherFormData) => {
		mutate(
			{ voucher: data.code },
			{
				onSuccess: () => {
					const userData = getUserData();

					sendGTMEvent({ ecommerce: null });
					sendGTMEvent({
						event: "voucher_code_applied",
						ecommerce: {
							voucher_code: data.code,
						},
						...userData,
					});
					reset();
				},
				onError: () => {
					setError("code", { message: t("Invalid voucher code") });
				},
			},
		);
	};

	return (
		<>
			<form onSubmit={handleSubmit(onSubmit)} className={styles.couponField}>
				<Input
					{...register("code")}
					className={clsx(styles.input, errors.code && styles.inputError)}
					placeholder={t("Enter your coupon code")}
				/>

				<Button
					loading={isPending || isFetching}
					className={styles.button}
					variant="community"
					type="submit"
				>
					<Translate>Add code</Translate>
				</Button>
			</form>
			{errors.code && (
				<p role="alert" className={styles.errorMessage}>
					{errors.code.message}
				</p>
			)}
		</>
	);
};

const VouchersList = () => {
	const { data } = useCart();
	const { mutate } = useRemoveVoucher();

	const vouchers = data?.vouchers?.filter((v) => v.type === "code") || [];

	if (!vouchers.length) return null;

	return (
		<div className={clsx(styles.textRight, styles.vouchersList)}>
			{vouchers.map((v, i) => (
				<div className={styles.voucher} key={i}>
					{v.description}
					<IconButton onClick={() => mutate({ voucher: v.voucher! })}>
						<XIcon />
					</IconButton>
				</div>
			))}
		</div>
	);
};

export interface CartSummaryProps extends ComponentProps<"dl"> {}

export const CartSummary = ({ className, ...props }: CartSummaryProps) => {
	const t = useTranslations();
	const { data, isLoading, error } = useCart();

	if (isLoading) {
		return <CartSummarySkeleton />;
	}

	if (error) {
		return null;
	}

	return (
		<dl {...props} className={clsx(className, styles.root)}>
			<div className={styles.row}>
				<details className="standard" style={{ width: "100%" }}>
					<summary>
						{t("Add coupon code")} <Chevron />
					</summary>
					<AddVoucher />
				</details>
			</div>
			<div className={styles.row}>
				<dt>{t("Subtotal")}</dt>
				<dd>{priceFormatter(data?.itemsTotalPriceAsNumber, data?.currency)}</dd>
			</div>
			{!!data?.totalDiscountPriceAsNumber && (
				<div className={styles.row}>
					<dt>{t("Discount")}</dt>
					<dd>
						<div className={styles.textRight}>
							-{priceFormatter(data.totalDiscountPriceAsNumber, data.currency)}
						</div>
						<VouchersList />
					</dd>
				</div>
			)}
			<div className={styles.row}>
				<dt>{t("Shipping")}</dt>
				<dd>
					{data?.shippingPriceAsNumber === 0
						? t("Free")
						: priceFormatter(data?.shippingPriceAsNumber, data?.currency)}
				</dd>
			</div>
			<div className={styles.row}>
				<dt>{t("Total")}</dt>
				<dd>{priceFormatter(data?.totalPriceAsNumber, data?.currency)}</dd>
			</div>
		</dl>
	);
};

export const CartSummarySkeleton = () => {
	return (
		<div className={styles.root}>
			<div className={styles.row}>
				<Skeleton fitContent>
					<span>Subtotal</span>
				</Skeleton>
				<Skeleton fitContent>
					<span>xxx xxx</span>
				</Skeleton>
			</div>
			<div className={styles.row}>
				<Skeleton fitContent>
					<span>Shipping</span>
				</Skeleton>
				<Skeleton fitContent>
					<span>xxx xxx</span>
				</Skeleton>
			</div>
			<div className={styles.row}>
				<Skeleton fitContent>
					<span>Total</span>
				</Skeleton>
				<Skeleton fitContent>
					<span>xxx xxx</span>
				</Skeleton>
			</div>
		</div>
	);
};
